import { inject } from "fw";
import { Store, handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { AtsRepository } from "network/ats-repository";
import { formatDate } from "views/components/date-range";
import { CurrentUserStore } from "./current-user";

export type LoadingStatus = "loading" | "loaded" | "error";

interface DashboardMetricsShape {
    atsOrganizationId: string;
    importAggregations: [];
    exportAggregations: [];
    lastLoaded: Date;
    status: LoadingStatus;
}

export class DashboardMetricsEnsureAction {
    constructor(public atsOrganizationId: string = null) {}
}

export class DashboardMetricsRefreshAction {}
export class DashboardMetricsResetAction {}

@inject
export class DashboardMetricsStore extends Store<DashboardMetricsShape> {
    constructor(
        private currentUser: CurrentUserStore,
        private atsRepo: AtsRepository
    ) {
        super();
    }

    defaultState(): DashboardMetricsShape {
        return {
            atsOrganizationId: null,
            importAggregations: [],
            exportAggregations: [],
            lastLoaded: null,
            status: null
        };
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.handleResetAction();
    }

    @handle(LogoutAction)
    private handleLogoutAction() {
        this.handleResetAction();
    }

    @handle(DashboardMetricsResetAction)
    private handleResetAction() {
        this.setState(() => this.defaultState());
    }

    @handle(DashboardMetricsEnsureAction)
    private async handleEnsureAction(action: DashboardMetricsEnsureAction) {
        const { atsOrganizationId, lastLoaded, status } = this.state;
        if (action.atsOrganizationId === atsOrganizationId && status === "loaded") {
            // Reload the state store if the data is older than 5 minutes.
            const fiveMinutesAgo = new Date();
            fiveMinutesAgo.setMinutes(fiveMinutesAgo.getMinutes() - 5);

            if (lastLoaded > fiveMinutesAgo) {
                return;
            }
        }

        this.setState(() => ({
            ...this.defaultState(),
            status: "loading",
            atsOrganizationId: action.atsOrganizationId
        }));

        await this.loadData();
    }

    @handle(DashboardMetricsRefreshAction)
    private async handleRefreshAction() {
        if (this.state.status === "loading") {
            return;
        }

        this.setState((state) => ({
            ...state,
            status: "loading"
        }));

        await this.loadData();
    }

    private async loadData() {
        const { atsOrganizationId } = this.state;

        const last30days = new Date();
        last30days.setDate(last30days.getDate() - 30);
        last30days.setHours(0, 0, 0, 0);
        const { timezone } = this.currentUser.state;

        try {
            const organizationFilter = atsOrganizationId ? `AND metaData.organizationId:${atsOrganizationId}` : "";
            const importFilter = `metaData.dateCreatedUtc:>=${formatDate(last30days)}^"${timezone}" ${organizationFilter}`.trimEnd();
            const importResponse = await this.atsRepo.atsApplicationsList(
                importFilter,
                null,
                null,
                null,
                null,
                null,
                `date:(metaData.dateCreatedUtc^"${timezone}" terms:(status))`,
                true
            );
            const importResult = importResponse.Aggregations["date_metaData.dateCreatedUtc"]?.Items;

            const exportFilter = `_missing_:parentId AND metaData.dateCreatedUtc:>=${formatDate(last30days)}^"${timezone}" ${organizationFilter}`.trimEnd();
            const exportResponse = await this.atsRepo.atsExportsList(
                exportFilter,
                null,
                null,
                null,
                null,
                null,
                null,
                `date:(metaData.dateCreatedUtc^"${timezone}" terms:(definition.format) sum:metaData.idCount)`,
                true
            );
            const exportResult = exportResponse.Aggregations["date_metaData.dateCreatedUtc"]?.Items;

            this.setState((state) => ({
                ...state,
                importAggregations: importResult,
                exportAggregations: exportResult,
                lastLoaded: new Date(),
                status: "loaded"
            }));
        } catch (err) {
            this.setState((state) => ({
                ...state,
                ats: null,
                contacts: null,
                status: "error"
            }));
        }
    }
}
